import { useState } from 'react'
import { ApiError } from '@/api/client'
import { teams as teamApi } from '@/api/endpoints'
import type { Team } from '@/api/types'
import { Err } from '@/components/Err'
import { ToneBadge } from '@/components/ToneBadge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { usePicked } from '@/hooks/usePicked'
import { useWorldRefresh } from '@/hooks/useWorld'
import { personRow } from '@/views/model'
import { TeamCreateForm, TeamDeleteForm } from '@/views/TeamForms'
import { ACTION, ACTION_DANGER, CHIP } from '@/views/vm/chips'
import { LIST, PeoplePicker, ROW } from './PeoplePicker'
import { pairs } from './teamPick'
import type { Candidate, Verb } from './teamPick'

// `팀 관리`(F-66) — 대상과 팀을 한 창에서 정한다. 대화 상자의 본문만이고 `<Dialog>` 는 `UserActions` 가 갖는다.
//
// 왼쪽이 대상인 사람들이고 오른쪽이 팀이다. **후보를 걸러 내지 않는다**: 팀은 늘 전부 선다
// (#370 — 목록을 소속으로 줄이면 "분명 있는데 없다" 가 된다). 줄에 소속을 세어 적지도 않는다 —
// 한 줄의 수가 무엇의 몇인지 그 자리에서 정해지지 않아 헷갈린다(#377).
// 왼쪽 칸은 `PeoplePicker` 다.
//
// 팀 칸의 `팀 생성`·`팀 삭제`(F-84)는 팀 화면과 같은 창(`TeamForms`)을 이 창 안에서 연다 — 닫으면 고르던 자리로
// 돌아오고 고른 사람과 팀은 그대로다. 만든 팀은 골라진 채 서서 곧바로 사람을 넣을 수 있다. 삭제의 대상은 체크한 팀이고
// 팀 화면의 `삭제` 와 같은 확인(목록을 보이고 묻는다)을 거친다.
// 사람을 넣고 빼는 버튼은 `팀에 넣기`·`팀에서 빼기` 다 — 팀 자체에 하는 `팀 생성`·`팀 삭제` 와 한 칸에 서므로
// `추가`·`제거` 로 부르면 어느 쪽이 사람에게 하는 일인지 낱말로 가려지지 않는다.

const NARROW = 'mx-auto grid w-full max-w-lg gap-4'

export function TeamPanel({ seed, teams, onDone }: { seed: Candidate[]; teams: Team[]; onDone: () => void }) {
  const who = usePicked(seed)
  const [teamIds, setTeamIds] = useState<string[]>([])
  const [busy, setBusy] = useState<Verb | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [dropped, setDropped] = useState<string[]>([])
  const [sub, setSub] = useState<'create' | 'delete' | null>(null)
  // 만들고 지운 것은 곧바로 목록에 선다 — 다음 폴링을 기다리면 한 일이 실패한 것처럼 보인다.
  const refreshWorld = useWorldRefresh()

  // 고른 사람의 소속은 매 렌더 지금 팀 목록에서 잇는다 — 넣기·빼기의 짝(`pairs`)이 고른 순간의 소속을 보면
  // 그 사이 다른 곳에서 그 팀에 든 사람에게 PUT 을 보내 역할을 덮어쓴다.
  const chosen = who.chosen.map((c) => personRow(c.user, teams))
  const picked = teams.filter((t) => teamIds.includes(t.id))

  // 뒤집기가 아니라 값 지정이다 — 줄과 체크박스 두 경로가 겹쳐 불려도 결과가 같다.
  const setTeam = (id: string, on: boolean) =>
    setTeamIds((v) => (on ? (v.includes(id) ? v : [...v, id]) : v.filter((x) => x !== id)))

  const run = async (verb: Verb) => {
    setBusy(verb)
    setError(null)
    setDropped([])
    const todo = pairs(verb, chosen, picked)
    let first: unknown = null
    const failed = new Set<string>()
    const gone = new Map<string, string>()
    for (const { user, team } of todo) {
      if (gone.has(user.id)) continue
      try {
        if (verb === 'add') await teamApi.setMember(team.id, user.id, 'member')
        else await teamApi.removeMember(team.id, user.id)
      } catch (e) {
        // 빼기의 404 는 이미 없다는 뜻이다 — 원하던 상태라 성공으로 센다.
        if (verb === 'remove' && e instanceof ApiError && e.status === 404) continue
        // 탈퇴했거나 비활성화된 계정(409 `user_inactive`)은 어느 팀에도 들 수 없다 — 고른 칸에서 빼고 한 줄로 말한다.
        // 고르는 자리의 찾기에 없는 사람이라 남겨 두면 풀 길 없이 넣기마다 같은 거절이다.
        if (e instanceof ApiError && e.reason === 'user_inactive') {
          gone.set(user.id, user.username)
          failed.delete(user.id)
          continue
        }
        if (first === null) first = e
        failed.add(user.id)
      }
    }
    setBusy(null)
    if (first !== null || gone.size > 0) {
      setError(first)
      setDropped([...gone.values()])
      // 된 사람은 빠지고 안 된 사람만 남는다 — 남은 것이 무엇인지 화면이 말한다.
      who.only(failed)
      return
    }
    onDone()
  }

  // 사람과 팀을 골랐으면 열린다. 이미 원하는 상태인 짝은 `pairs` 가 빼므로 아무것도 보내지 않고 닫힌다 —
  // 결과가 곧 원하던 상태라 말할 것이 없다.
  const ready = chosen.length > 0 && picked.length > 0

  // 이 창은 두 칸이라 넓다 — 한 칸짜리 창은 그 폭 가운데에 팀 화면의 창과 같은 폭으로 선다.
  if (sub === 'create') {
    return (
      <div className={NARROW}>
        <TeamCreateForm
          onClose={() => setSub(null)}
          onDone={(team) => {
            setSub(null)
            setTeam(team.id, true)
            refreshWorld()
          }}
        />
      </div>
    )
  }
  if (sub === 'delete') {
    return (
      <div className={NARROW}>
        <TeamDeleteForm
          teams={picked}
          onClose={() => setSub(null)}
          onDone={() => {
            setSub(null)
            setTeamIds([])
            refreshWorld()
          }}
        />
      </div>
    )
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>팀 관리</DialogTitle>
      </DialogHeader>

      <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
        <PeoplePicker id="team-manage-users" teams={teams} chosen={who.chosen} onMember={who.set} onFound={who.renew} />

        <div className="grid content-start gap-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <Label id="team-manage-teams">팀</Label>
            <span className="ml-auto flex items-center gap-1.5">
              <button type="button" className={CHIP} disabled={busy !== null} onClick={() => setSub('create')}>
                팀 생성
              </button>
              <button
                type="button"
                className={`${ACTION} ${ACTION_DANGER}`}
                disabled={picked.length === 0 || busy !== null}
                onClick={() => setSub('delete')}
              >
                팀 삭제
              </button>
            </span>
          </div>
          <ul aria-labelledby="team-manage-teams" className={LIST}>
            {teams.length === 0 ? (
              <li className="px-3 py-2 text-[12px] text-muted-foreground">팀 없음</li>
            ) : (
              teams.map((t) => (
                <li key={t.id} className={ROW} onClick={() => setTeam(t.id, !teamIds.includes(t.id))}>
                  <span className="contents" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      aria-label={t.display_name}
                      checked={teamIds.includes(t.id)}
                      onCheckedChange={(on) => setTeam(t.id, on === true)}
                    />
                  </span>
                  <span className="truncate">{t.display_name}</span>
                  {!t.enabled ? <ToneBadge tone="warn">비활성</ToneBadge> : null}
                </li>
              ))
            )}
          </ul>
          <div className="flex justify-end gap-2">
            <Button type="button" size="sm" variant="outline" disabled={!ready || busy !== null} onClick={() => void run('remove')}>
              {busy === 'remove' ? '빼는 중' : '팀에서 빼기'}
            </Button>
            <Button type="button" size="sm" disabled={!ready || busy !== null} onClick={() => void run('add')}>
              {busy === 'add' ? '넣는 중' : '팀에 넣기'}
            </Button>
          </div>
        </div>
      </div>

      {dropped.length > 0 ? (
        <p role="status" className="text-[13px] text-warn-ink">{`탈퇴했거나 비활성화된 ${dropped.join(', ')} 의 고르기를 풀었습니다.`}</p>
      ) : null}
      <Err error={error} />
      <DialogFooter>
        <Button type="button" variant="outline" disabled={busy !== null} onClick={onDone}>
          닫기
        </Button>
      </DialogFooter>
    </>
  )
}
